import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { type IEnvironmentProvider, loadEntryPoint } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import ObjectIcon from '@atlaskit/icon-lab/core/assets-object';
import GoalIcon from '@atlaskit/icon/core/goal';
import PageIcon from '@atlaskit/icon/core/page';
import ProjectIcon from '@atlaskit/icon/core/project';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import TeamsIcon from '@atlaskit/icon/core/teams';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { fg } from '@atlaskit/platform-feature-flags';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import { AnyAri } from '@atlassian/ari';
import type { PanelDataMap } from '@atlassian/confluence-page-preview-panel';
import { confluencePagePreviewPanelEntryPoint } from '@atlassian/confluence-page-preview-panel/entrypoint';
import atlasGoalEntryPoint from '@atlassian/goal-panel/atlas-goal-entrypoint';
import objectPreviewPanelEntryPoint from '@atlassian/object-preview-panel/object-preview-panel-entrypoint';
import type { ActivityTabSlot } from '@atlassian/object-preview-panel/types';
import { type PanelProduct, usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import atlasProjectEntryPoint from '@atlassian/project-panel/atlas-project-entrypoint';
import { getDefaultSelectedTab } from '@atlassian/projects-goals-common/tabs';
import {
	AtlassianRelayEnvironmentProvider,
	useRelayEnvironmentProvider,
} from '@atlassian/relay-environment-provider';
import teamPreviewPanelEntryPoint from '@atlassian/team-preview-panel/team-preview-panel-entrypoint';

import { ALL_SUPPORTED_OBJECTS } from './constants';
import messages from './messages';
import { EntryPointPreviewPanel } from './panels/EntryPointPreviewPanel';
import { IFramePreviewPanel } from './panels/IFramePreviewPanel';
import type { GlobalObjectType } from './types';
import { buildIssueViewEmbedUrl, isInIframe } from './utils';

export type Product = 'jira' | 'confluence' | 'teams' | 'home';

type Options = {
	activityTabSlot?: ActivityTabSlot;
	analyticsAttributes?: AnalyticsAttributes;
	enabled?: boolean;
	/**
	 * Optional override for the Relay environment provider used to load entry-point queries.
	 *
	 * By default, the closest `RelayEnvironmentProvider` in the React tree is used. Pass an
	 * explicit provider when the surrounding environment is not the AGG environment that the
	 * preview-panel entry-points expect (e.g. when sitting inside a product-specific Relay
	 * environment that does not support AGG persisted queries).
	 */
	environmentProvider?: IEnvironmentProvider<Record<string, never>>;
	rootSourceScreen?: string;
	supportedObjects?: GlobalObjectType[];
};

type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;
type OpenPreviewPanel = (props: {
	ari: string;
	name: string;
	onOpen?: () => void;
	panelData?: PanelDataMap;
	parentProduct?: string;
	url: string;
}) => void;

export type useGlobalObjectPreviewPanelsReturnType = {
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
	openPreviewPanel: OpenPreviewPanel;
};

const getGlobalObjectType = (ari: string): GlobalObjectType => {
	const parsedAri = AnyAri.parse(ari);
	return `${parsedAri.resourceOwner}-${parsedAri.resourceType}` as GlobalObjectType;
};

export const useGlobalObjectPreviewPanels = (
	product: Product,
	cloudId: string,
	options?: Options,
): useGlobalObjectPreviewPanelsReturnType => {
	const {
		enabled = true,
		activityTabSlot,
		supportedObjects = ALL_SUPPORTED_OBJECTS,
		analyticsAttributes,
		rootSourceScreen,
		environmentProvider: environmentProviderOverride,
	} = options ?? {};
	const defaultEnvironmentProvider = useRelayEnvironmentProvider();
	const environmentProvider = environmentProviderOverride ?? defaultEnvironmentProvider;
	const { formatMessage } = useIntl();
	const { open } = usePreviewPanelsActions();
	const enableDiscoverPreviewPanelsExp = FeatureGates.getExperimentValue(
		'townsquare_enable_discover_preview_panels_exp',
		'cohort',
		'control',
		{
			fireExperimentExposure: false,
		},
	);

	const isPreviewPanelAvailable = useCallback(
		({ ari }: { ari: string }): boolean => {
			const panelType = getGlobalObjectType(ari);
			const panelEnabled = enabled && supportedObjects.includes(panelType);

			if (panelType === 'confluence-blogpost') {
				return panelEnabled && enableDiscoverPreviewPanelsExp !== 'control';
			}

			return panelEnabled;
		},
		[enabled, supportedObjects, enableDiscoverPreviewPanelsExp],
	);

	const openPreviewPanel = useCallback(
		({
			url,
			ari,
			name,
			parentProduct,
			panelData,
			onOpen,
		}: {
			ari: string;
			name: string;
			onOpen?: () => void;
			panelData?: PanelDataMap;
			parentProduct?: string;
			url: string;
		}): void => {
			const globalObjectType = getGlobalObjectType(ari);

			if (!isPreviewPanelAvailable({ ari })) {
				throw new Error(`Preview panel for ${globalObjectType} with ARI ${ari} is not supported.`);
			}

			// Check if we're in an iframe (not the top-most frame)
			if (isInIframe()) {
				// Post message to parent frame to open the panel
				window.parent.postMessage(
					{
						type: 'OPEN_PREVIEW_PANEL',
						payload: {
							url,
							ari,
							name,
							panelData,
						},
					},
					'*',
				);
				return;
			}

			let panelContent;
			let panelProduct: PanelProduct;
			switch (globalObjectType) {
				case 'jira-issue':
					const JiraIcon = product === 'jira' ? WorkItemIcon : WorkItemsIcon;
					const title = formatMessage(messages.workItem);

					panelContent = (
						<IFramePreviewPanel
							name={title}
							url={url}
							iframeUrl={
								// Availability check only — this decides whether the embed URL carries the
								// rootSourceScreen, NOT whether the user is accessing the Capacity
								// planning experience. We therefore read the GA experiment WITHOUT
								// firing a Statsig exposure (fireExperimentExposure: false); exposure is
								// fired at the point the user actually opens the Capacity experience,
								// keeping the GA experiment's population clean.
								//
								// The OR still short-circuits: when the legacy EAP gate is ON the
								// experiment is never read at all. EAP customers remain unconditionally
								// enabled.
								fg('add_capacity_tab_to_the_board_horizontal_nav') ||
								FeatureGates.getExperimentValue(
									'individual_capacity_planning_ga',
									'isEnabled',
									false,
									{ fireExperimentExposure: false },
								)
									? buildIssueViewEmbedUrl(url, supportedObjects, rootSourceScreen)
									: buildIssueViewEmbedUrl(url, supportedObjects)
							}
							icon={<JiraIcon label="" />}
							panelType="jira-work-item"
							product="jira"
							parentProduct={parentProduct}
							ari={ari}
							// Availability check only — this decides whether to forward the
							// analyticsAttributes prop, NOT whether the user is accessing the Capacity
							// planning experience. Same no-exposure semantics as the iframeUrl prop
							// above: we read the GA experiment WITHOUT firing a Statsig exposure
							// (fireExperimentExposure: false). The OR still short-circuits when the
							// legacy EAP gate is ON; EAP customers remain unconditionally enabled.
							{...(fg('add_capacity_tab_to_the_board_horizontal_nav') ||
							FeatureGates.getExperimentValue(
								'individual_capacity_planning_ga',
								'isEnabled',
								false,
								{ fireExperimentExposure: false },
							)
								? { analyticsAttributes }
								: {})}
						/>
					);
					panelProduct = 'jira';
					break;
				case 'identity-team': {
					const teamEntryPointReference = loadEntryPoint<typeof teamPreviewPanelEntryPoint>(
						environmentProvider,
						teamPreviewPanelEntryPoint,
						{ cloudId },
					);
					const teamPanel = (
						<EntryPointPreviewPanel
							entryPoint={teamPreviewPanelEntryPoint}
							preloadedEntryPointReference={teamEntryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								ari,
								cloudId,
								openPreviewPanel,
							}}
							globalObject={{
								name: formatMessage(messages.team),
								url: url,
								icon: <TeamsIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						teamPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{teamPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'people';
					break;
				}
				case 'townsquare-goal': {
					const entryPointReference = loadEntryPoint<typeof atlasGoalEntryPoint>(
						environmentProvider,
						atlasGoalEntryPoint,
						{ ari },
					);
					const goalPanel = (
						<EntryPointPreviewPanel
							entryPoint={atlasGoalEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.goal),
								url: url,
								icon: <GoalIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						goalPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{goalPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'townsquare-project': {
					const entryPointReference = loadEntryPoint<typeof atlasProjectEntryPoint>(
						environmentProvider,
						atlasProjectEntryPoint,
						{ ari },
					);
					const projectPanel = (
						<EntryPointPreviewPanel
							entryPoint={atlasProjectEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.project),
								url: url,
								icon: <ProjectIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						projectPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{projectPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'confluence-blogpost':
				case 'confluence-page': {
					const isBlogPost = globalObjectType === 'confluence-blogpost';

					// Fall through if blogpost experiment is not enabled and we are trying to render a blogpost preview panel
					if (isBlogPost && enableDiscoverPreviewPanelsExp === 'control') {
						return;
					}

					const urlObj = new URL(url);
					urlObj.searchParams.set(
						'previewPanels',
						encodeURIComponent(ALL_SUPPORTED_OBJECTS.join(',')),
					);
					const formattedUrl = urlObj.toString();
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url: formattedUrl,
								parentProduct: product,
								contentType: isBlogPost ? 'blogpost' : 'page',
								panelData,
								cloudId,
							}}
							globalObject={{
								name: formatMessage(isBlogPost ? messages.blogPost : messages.page),
								url: formattedUrl,
								icon: isBlogPost ? <QuotationMarkIcon label="" /> : <PageIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'confluence-whiteboard': {
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
							}}
							globalObject={{
								name: formatMessage(messages.whiteboard),
								url: url,
								icon: <WhiteboardIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'cmdb-object': {
					const assetsEntryPointReference = loadEntryPoint<typeof objectPreviewPanelEntryPoint>(
						environmentProvider,
						objectPreviewPanelEntryPoint,
						{ cloudId },
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={objectPreviewPanelEntryPoint}
							preloadedEntryPointReference={assetsEntryPointReference}
							runtimeProps={{
								activityTabSlot,
								url,
								parentProduct: product,
								ari,
								cloudId,
								openPreviewPanel,
							}}
							// These are fallback properties
							globalObject={{
								name: formatMessage(messages.object),
								url: url,
								icon: <ObjectIcon label="" />,
							}}
						/>
					);
					panelProduct = 'assetsPreviewPanel';
					break;
				}
				default:
					throw new Error(`Unsupported global object type: ${globalObjectType}`);
			}

			open({
				panel: {
					id: ari,
					type: globalObjectType,
					content: () => panelContent,
					product: panelProduct,
				},
				...(fg('jivtech-1920-back-navigation-gpp') ? { onOpen } : {}),
			});
		},
		[
			isPreviewPanelAvailable,
			enableDiscoverPreviewPanelsExp,
			open,
			formatMessage,
			supportedObjects,
			environmentProvider,
			environmentProviderOverride,
			product,
			cloudId,
			activityTabSlot,
			analyticsAttributes,
			rootSourceScreen,
		],
	);

	return {
		isPreviewPanelAvailable,
		openPreviewPanel,
	};
};

const isAllowedOrigin = (origin: string): boolean => {
	return origin === window.location.origin;
};

export const usePreviewPanelIFrameCommunication = (openPreviewPanel: OpenPreviewPanel): void => {
	// Listen for messages from iframes to open panels
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			// Validate origin FIRST - reject messages from untrusted origins
			// Protected by feature flag: vuln-fix-adding-origin-validation-for-iframe-pp
			if (!isAllowedOrigin(event.origin) && fg('vuln-fix-adding-origin-validation-for-iframe-pp')) {
				return;
			}

			// Only handle messages with the expected type
			if (event.data?.type !== 'OPEN_PREVIEW_PANEL') {
				return;
			}

			const { payload } = event.data;
			if (payload?.url && payload?.ari && payload?.name) {
				openPreviewPanel({
					url: payload.url,
					ari: payload.ari,
					name: payload.name,
					panelData: payload.panelData,
				});
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [openPreviewPanel]);
};
